import { FormGroup } from '@angular/forms';
import { updateVerbaleOnStore } from '@core/helpers/create-validator.helper';
import { Store } from '@ngrx/store';
import { Revisione53State } from 'app/features/revisione53/core/store/revisione53.reducers';
import { cloneDeep } from 'lodash-es';
import { getControl } from '@shared/helpers/form.helper';
import { isNilOrEmpty, toBoolean } from '@shared/helpers/utils.helper';
import { MyVerbale } from '../../../../../core/models/interfaces/revisione.interface';
import { formPaths } from '@shared/constants/form-paths.const';

/** Ricalcola l'obbligatorietà di tutte e 10 le schede di settore. Innescata da campi di
 * Dati Identificativi (my_TipoSezEnte, my_ckSezAlboCorretta, my_SezEnte) e dai checkbox
 * "allega scheda" di Scheda Altre Coop / my_ModoDiAssociazione di Scheda Trasporto — i listener
 * che la chiamano vivono in coop-trasporto-specific-listeners.ts e coop-altre-coop-specific.listeners.ts,
 * non qui: rinominare/rimuovere uno di quei campi senza aggiornare quei due file rompe silenziosamente
 * l'obbligatorietà di tutte le schede. */
export const defineObbligatorietaSchedeSettore = (_form: FormGroup, store: Store<Revisione53State>) => {
    updateVerbaleOnStore(store, (myVerbale) => {
        const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
        const sez = myVerbaleClone.my_SezioneI__Rilevazione;
        const { my_TipoSezEnte, my_ckSezAlboCorretta } = sez.my_DatiIdentificativi;
        const altreCoop = sez.my_SchedaCoopAltreCoop;

        sez.my_SchedaCoopSociali.my_ScSocObbl = '0';
        sez.my_SchedaCoopEdil.my_ScEdilObbl = '0';
        sez.my_SchedaCoopConsumo.my_ScConsumoObbl = '0';
        sez.my_SchedaCoopPDL.my_ScPDLObbl = '0';
        sez.my_SchedaCoopAgricole.my_ScAgricoleObbl = '0';
        sez.my_SchedaCoopTrasporto.my_ScTrasportoObbl = '0';
        sez.my_SchedaCoopPesca.my_ScPescaObbl = '0';
        sez.my_SchedaCoopAltreCoop.my_ScAltreCoopObbl = '0';
        sez.my_SchedaCoopFidi.my_ScCoopFidiObbl = '0';
        sez.my_SchedaCoopDettaglianti.my_ScCoopDettagliObbl = '0';

        if (my_TipoSezEnte !== '') {
            if (my_ckSezAlboCorretta === '1') {
                switch (my_TipoSezEnte) {
                    case '1':
                        sez.my_SchedaCoopEdil.my_ScEdilObbl = '1';
                        break;
                    case '2':
                        sez.my_SchedaCoopSociali.my_ScSocObbl = '1';
                        break;
                    case '4':
                        sez.my_SchedaCoopConsumo.my_ScConsumoObbl = '1';
                        break;
                    case '5':
                        sez.my_SchedaCoopPDL.my_ScPDLObbl = '1';
                        break;
                    case '6':
                    case '7':
                    case '14':
                        sez.my_SchedaCoopAgricole.my_ScAgricoleObbl = '1';
                        break;
                    case '8':
                        // VBS confronta my_ModoDiAssociazione coi 3 valori letterali ("Cooperativa di servizio"/
                        // "di lavoro"/"Entrambe le tipologie"); qui equivale a "non vuoto" SOLO perché il
                        // select in scheda-coop-trasporto.component.ts (itemsSociCooperativaServizio) non espone
                        // altri valori non-vuoti — se si aggiunge una 4a opzione, va ripristinato il confronto esplicito.
                        if (!isNilOrEmpty(sez.my_SchedaCoopTrasporto.my_ModoDiAssociazione)) {
                            sez.my_SchedaCoopTrasporto.my_ScTrasportoObbl = '1';
                        }
                        break;
                    case '9':
                        sez.my_SchedaCoopPesca.my_ScPescaObbl = '1';
                        break;
                    case '11':
                        sez.my_SchedaCoopFidi.my_ScCoopFidiObbl = '1';
                        break;
                    case '15':
                        sez.my_SchedaCoopDettaglianti.my_ScCoopDettagliObbl = '1';
                        break;
                }
            }

            if (my_TipoSezEnte === '10' || my_ckSezAlboCorretta === '0') {
                sez.my_SchedaCoopAltreCoop.my_ScAltreCoopObbl = '1';

                if (toBoolean(altreCoop.my_CkAltreCoopAllegaSchedaEdilizie)) {
                    sez.my_SchedaCoopEdil.my_ScEdilObbl = '1';
                }
                if (toBoolean(altreCoop.my_CkAltreCoopAllegaSchedaSociali)) {
                    sez.my_SchedaCoopSociali.my_ScSocObbl = '1';
                }
                if (toBoolean(altreCoop.my_CkAltreCoopAllegaSchedaConsumo)) {
                    sez.my_SchedaCoopConsumo.my_ScConsumoObbl = '1';
                }
                if (toBoolean(altreCoop.my_CkAltreCoopAllegaSchedaTrasporto)) {
                    sez.my_SchedaCoopTrasporto.my_ScTrasportoObbl = '1';
                }
                if (toBoolean(altreCoop.my_CkAltreCoopAllegaSchedaPDL)) {
                    sez.my_SchedaCoopPDL.my_ScPDLObbl = '1';
                }
                if (toBoolean(altreCoop.my_CkAltreCoopAllegaSchedaGaranziaFidi)) {
                    sez.my_SchedaCoopFidi.my_ScCoopFidiObbl = '1';
                }
                if (toBoolean(altreCoop.my_CkAltreCoopAllegaSchedaPesca)) {
                    sez.my_SchedaCoopPesca.my_ScPescaObbl = '1';
                }
                if (toBoolean(altreCoop.my_CkAltreCoopAllegaSchedaDettaglianti)) {
                    sez.my_SchedaCoopDettaglianti.my_ScCoopDettagliObbl = '1';
                }
                if (
                    toBoolean(altreCoop.my_CkAltreCoopAllegaSchedaAgricole) ||
                    toBoolean(altreCoop.my_CkAltreCoopAllegaSchedaAgricoleConferimento) ||
                    toBoolean(altreCoop.my_CkAltreCoopAllegaSchedaAgricoleConsorzi)
                ) {
                    sez.my_SchedaCoopAgricole.my_ScAgricoleObbl = '1';
                }
            }
        }

        const paths: [string, string][] = [
            [`${formPaths.schedaCoopSociali}.my_ScSocObbl`, sez.my_SchedaCoopSociali.my_ScSocObbl],
            [`${formPaths.schedaCoopEdil}.my_ScEdilObbl`, sez.my_SchedaCoopEdil.my_ScEdilObbl],
            [`${formPaths.schedaCoopConsumo}.my_ScConsumoObbl`, sez.my_SchedaCoopConsumo.my_ScConsumoObbl],
            [`${formPaths.schedaCoopPDL}.my_ScPDLObbl`, sez.my_SchedaCoopPDL.my_ScPDLObbl],
            [`${formPaths.schedaCoopAgricole}.my_ScAgricoleObbl`, sez.my_SchedaCoopAgricole.my_ScAgricoleObbl],
            [`${formPaths.schedaCoopTrasporto}.my_ScTrasportoObbl`, sez.my_SchedaCoopTrasporto.my_ScTrasportoObbl],
            [`${formPaths.schedaCoopPesca}.my_ScPescaObbl`, sez.my_SchedaCoopPesca.my_ScPescaObbl],
            [`${formPaths.schedaAltreCoop}.my_ScAltreCoopObbl`, sez.my_SchedaCoopAltreCoop.my_ScAltreCoopObbl],
            [`${formPaths.schedaCoopFidi}.my_ScCoopFidiObbl`, sez.my_SchedaCoopFidi.my_ScCoopFidiObbl],
            [`${formPaths.schedaCoopDettaglianti}.my_ScCoopDettagliObbl`, sez.my_SchedaCoopDettaglianti.my_ScCoopDettagliObbl],
        ];
        paths.forEach(([path, value]) => {
            return getControl(_form, path).patchValue(value);
        });
        return myVerbaleClone;
    });
};
